import { z } from "zod";

/**
 * Échanges avec les avocats (devis, module « Échanges avec les avocats ») :
 * liens sécurisés de récupération ou de dépôt de documents, protégés par un
 * code envoyé au courriel du destinataire, à durée limitée, et qui expirent
 * après téléchargement.
 */
export const LINK_DIRECTIONS = ["telechargement", "depot"] as const;
export type LinkDirection = (typeof LINK_DIRECTIONS)[number];
export const LINK_DIRECTION_LABELS: Record<LinkDirection, string> = {
  telechargement: "Récupération de documents",
  depot: "Dépôt de documents",
};

export const createShareLinkSchema = z.object({
  candidateId: z.string().min(1),
  direction: z.enum(LINK_DIRECTIONS),
  /** Pièces partagées (téléchargement). Vide pour un dépôt. */
  documentIds: z.array(z.string().min(1)).max(50).default([]),
  /** Courriel du destinataire : le code de vérification y est envoyé. */
  email: z.string().trim().email(),
  /** Durée de validité en heures (défaut 72, maximum 30 jours). */
  hours: z.coerce.number().int().min(1).max(720).default(72),
  /** Nombre de téléchargements autorisés par pièce (défaut 1 : le lien expire après téléchargement). */
  maxUses: z.coerce.number().int().min(1).max(10).default(1),
  message: z.string().trim().max(1000).optional().nullable(),
});
export type CreateShareLinkInput = z.infer<typeof createShareLinkSchema>;

export const verifyLinkCodeSchema = z.object({
  code: z
    .string()
    .trim()
    .regex(/^\d{6}$/, "code à six chiffres"),
});

export interface ShareLink {
  id: string;
  candidateId: string;
  candidateReference?: string;
  direction: LinkDirection;
  documentIds: string[];
  email: string;
  message: string | null;
  createdBy: string | null;
  createdByName?: string | null;
  createdAt: number | string;
  expiresAt: number | string;
  maxUses: number;
  uses: number;
  consumedAt: number | string | null;
  revokedAt: number | string | null;
  lastAccessAt: number | string | null;
  /** Présent uniquement dans la réponse de création (jamais réaffiché). */
  url?: string;
}

/** Ce que voit le destinataire du lien avant et après vérification. */
export interface PublicLinkView {
  direction: LinkDirection;
  state: "valide" | "expire" | "revoque" | "consomme";
  verified: boolean;
  message: string | null;
  organisationName: string;
  /** Visible une fois le code vérifié. */
  documents?: { id: string; label: string; originalName: string; size: number; remainingUses: number }[];
}
